import React, {Suspense, useEffect} from 'react';

import {Route, BrowserRouter, Routes} from 'react-router-dom';
import {Helmet, HelmetProvider} from 'react-helmet-async';

import {ThemeProvider} from '../contexts/ThemeContext';
import Loader from '../components/common/Loader';
import AuthWrapper from '../components/AuthWrapper';
import {METADATA_DESCRIPTION} from '../constants';
import * as Sentry from '../utils/sentry';
import {getAppEnv} from '../utils/utils';
import ErrorBoundary, {ErrorFallbackScreen, RouteErrorBoundary} from '../components/common/ErrorBoundary';
import lazyWithCatalog, {startCatalogLoad} from '../utils/lazyWithCatalog';
import LibVersionBadge from '../components/common/LibVersionBadge';
import {LVE_DASHBOARD_PATH, LVE_MEETING_PATH} from '../components/LveDashboard/paths';

Sentry.init();

const appEnv = getAppEnv();
const SMALL_FAVICON_PATH = `/assets/${appEnv}-favicon-16.svg`;
const MEDIUM_FAVICON_PATH = `/assets/${appEnv}-favicon-48.svg`;

// Every route reads catalog data (directly or via draft-editor/3d-lib), so each one
// gates on the catalog load; the shell and the auth gate render without it.
// These routes never read catalog data — plain lazy, no catalog gate (don't make them
// wait on the ~1.26MB catalog load they don't use).
const Dashboard = React.lazy(() => import('../components/Dashboard'));
// The DET owns its catalog init (runInitialTasks → catalogInit) and its own loading UI,
// so it loads with a plain lazy — no route-level catalog gate.
const DraftEditor = React.lazy(() => import('../components/DraftEditorWrapper'));
const CompareView = lazyWithCatalog(() => import('../components/CompareViewWrapper'));
const StyleBrowser = lazyWithCatalog(() => import('../components/StyleBrowserWrapper'));
const Sandbox = React.lazy(() => import('../components/SandboxWrapper'));
const Shortener = React.lazy(() => import('../components/ShortenerWrapper'));
const StyleEditor = lazyWithCatalog(() => import('../components/StyleEditorWrapper'));
const StyleSelector = lazyWithCatalog(() => import('../components/StyleSelectorWrapper'));
const MaterialList = lazyWithCatalog(() => import('../components/MaterialListWrapper'));
const RulesGallery = lazyWithCatalog(() => import('../components/RulesGalleryWrapper'));
const CatalogBrowser = lazyWithCatalog(() => import('../components/CatalogBrowserWrapper'));
const MapViewer = lazyWithCatalog(() => import('../components/MapViewerWrapper'));
const InventoryGallery = lazyWithCatalog(() => import('../components/InventoryGalleryWrapper'));
const BriefMode = lazyWithCatalog(() => import('../components/BriefModeWrapper'));
const PricingTester = lazyWithCatalog(() => import('../components/PricingTesterWrapper'));
const PermitCanvas = lazyWithCatalog(() => import('../components/PermitCanvasWrapper'));
const LveDashboard = React.lazy(() => import('../components/LveDashboard'));
const LveMeetingRecording = React.lazy(() => import('../components/LveDashboard/MeetingRecording'));

const App = () => {
  // Kick off the catalog download right after first paint, in parallel with auth,
  // so route gates almost never wait. Failures surface when a route needs the data.
  useEffect(() => {
    startCatalogLoad().catch(() => undefined);
  }, []);

  return (
    <ThemeProvider>
      <BrowserRouter>
        <HelmetProvider>
          <Helmet defaultTitle="Ergeon" titleTemplate="%s | Ergeon">
            <link href={SMALL_FAVICON_PATH} rel="icon" sizes="16x16" type="image/svg+xml" />
            <link href={MEDIUM_FAVICON_PATH} rel="icon" sizes="48x48" type="image/svg+xml" />
            <meta content={METADATA_DESCRIPTION} name="description"></meta>
          </Helmet>
          <Suspense fallback={<Loader />}>
            {/* Last-resort boundary; RouteErrorBoundary isolates per-route crashes first.
                The fallback still offers a way out — a root crash leaves nothing else to
                click, and "Back to Dashboard" is a full document load, so it also recovers
                from a broken bundle. Reported as unhandled despite that fallback: reaching
                here means the app is gone, which is the signal crash-rate and user-misery
                are built on. Route-level crashes stay handled. */}
            <ErrorBoundary boundary="root" fallback={<ErrorFallbackScreen showNavigation />} handled={false}>
              <AuthWrapper>
                <Routes>
                  {/* Pathless layout route: every route renders inside a per-route Sentry boundary. */}
                  <Route element={<RouteErrorBoundary />}>
                    <Route element={<Shortener />} path="/shortener" />
                    <Route element={<StyleBrowser />} path="/style-browser" />
                    <Route element={<StyleEditor />} path="/style-editor" />
                    <Route element={<StyleSelector />} path="/style-selector" />
                    <Route element={<BriefMode />} path="/brief-mode/:quoteId" />
                    <Route element={<BriefMode />} path="/brief-mode" />
                    <Route element={<Sandbox />} path="/sandbox" />
                    <Route element={<MaterialList />} path="/material-list" />
                    <Route element={<CompareView />} path="/draft-editor/compare/:leftId/:rightId" />
                    <Route element={<DraftEditor />} path="/draft-editor/:quoteId" />
                    <Route element={<DraftEditor />} path="/draft-editor" />
                    <Route element={<RulesGallery />} path="/rules-gallery" />
                    <Route element={<CatalogBrowser />} path="/catalog-browser" />
                    <Route element={<MapViewer />} path="/map-viewer/:quoteId" />
                    <Route element={<MapViewer />} path="/map-viewer" />
                    <Route element={<InventoryGallery />} path="/inventory-gallery" />
                    <Route element={<PricingTester />} path="/pricing-tester/:quoteId" />
                    <Route element={<PricingTester />} path="/pricing-tester" />
                    <Route element={<PermitCanvas />} path="/permit-canvas/:quoteId" />
                    <Route element={<PermitCanvas />} path="/permit-canvas" />
                    <Route element={<LveMeetingRecording />} path={LVE_MEETING_PATH} />
                    <Route element={<LveDashboard />} path={LVE_DASHBOARD_PATH} />
                    <Route element={<Dashboard />} path="/" />
                  </Route>
                </Routes>
              </AuthWrapper>
            </ErrorBoundary>
          </Suspense>
          <LibVersionBadge />
        </HelmetProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
};

export default App;
